import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';

const toNumber = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLabel = (value, index, prefix = 'Point') => {
  if (value == null) return `${prefix} ${index + 1}`;
  const text = String(value).trim();
  return text.length > 0 ? text : `${prefix} ${index + 1}`;
};

const buildXYSeries = (labels, values) => {
  return labels.map((label, index) => ({
    x: label,
    y: toNumber(values?.[index]) ?? 0,
  }));
};

const buildConfidenceBand = (labels, centerSeries, upperInput, lowerInput) => {
  const center = Array.isArray(centerSeries) ? centerSeries : [];
  const fallbackUpper = center.map((value) => Math.round((toNumber(value) ?? 0) * 1.12));
  const fallbackLower = center.map((value) => Math.round((toNumber(value) ?? 0) * 0.88));

  const upper = Array.isArray(upperInput) && upperInput.length === center.length
    ? upperInput.map((value, index) => toNumber(value) ?? fallbackUpper[index] ?? 0)
    : fallbackUpper;

  const lower = Array.isArray(lowerInput) && lowerInput.length === center.length
    ? lowerInput.map((value, index) => toNumber(value) ?? fallbackLower[index] ?? 0)
    : fallbackLower;

  return labels.map((label, index) => {
    const low = Math.min(lower[index] ?? 0, upper[index] ?? 0);
    const high = Math.max(lower[index] ?? 0, upper[index] ?? 0);
    return {
      x: label,
      y: [Math.max(0, low), Math.max(0, high)],
    };
  });
};

const formatTooltipUnits = (value, fallback = '-') => {
  const numeric = toNumber(value);
  if (numeric == null) return fallback;
  return `${(numeric / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K units`;
};

const formatTooltipBand = (start, end) => {
  const low = toNumber(start);
  const high = toNumber(end);
  if (low == null || high == null) return '-';
  return `${(low / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K - ${(high / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K units`;
};

const buildRenderedGraphSummary = (labels, values, lowerValues, upperValues, visibleRange = null) => {
  const points = labels.map((label, index) => ({
    date: label,
    total: toNumber(values[index]) ?? 0,
    base: null,
    promo: null,
    lower: toNumber(lowerValues[index]) ?? null,
    upper: toNumber(upperValues[index]) ?? null,
    index,
  }));

  let visiblePoints = points;
  if (visibleRange && Number.isFinite(visibleRange.min) && Number.isFinite(visibleRange.max)) {
    const minIndex = Math.max(0, Math.floor(visibleRange.min));
    const maxIndex = Math.min(points.length - 1, Math.ceil(visibleRange.max));
    visiblePoints = points.filter((point) => point.index >= minIndex && point.index <= maxIndex);
  }

  const scoped = visiblePoints.length ? visiblePoints : points;
  const sortedByTotal = [...scoped].sort((a, b) => b.total - a.total);
  const totals = scoped.map((point) => point.total);
  const totalDemand = totals.reduce((sum, value) => sum + value, 0);

  return {
    period_count: scoped.length,
    first_period: scoped[0]?.date || null,
    last_period: scoped[scoped.length - 1]?.date || null,
    total_visible_demand: totalDemand,
    total_base_demand: null,
    total_promotional_demand: null,
    promo_days_or_periods: null,
    promo_to_base_ratio: null,
    average_visible_demand: scoped.length ? totalDemand / scoped.length : null,
    highest_points: sortedByTotal.slice(0, 3),
    lowest_points: [...scoped].sort((a, b) => a.total - b.total).slice(0, 3),
    visible_points: scoped.map((point) => ({
      date: point.date,
      total: point.total,
      base: point.base,
      promo: point.promo,
      lower: point.lower,
      upper: point.upper,
      index: point.index,
    })),
    confidence_band_sample: scoped.slice(0, 5).map((point) => ({
      date: point.date,
      lower: point.lower,
      upper: point.upper,
    })),
    source: visibleRange ? 'simulation_chart_viewport' : 'simulation_chart_rendered',
  };
};

const getTooltipLabel = (w, dataPointIndex) => {
  const categories = w?.config?.xaxis?.categories;
  const fromCategories = Array.isArray(categories) ? categories[dataPointIndex] : null;
  const fromGlobals =
    w?.globals?.categoryLabels?.[dataPointIndex] ||
    w?.globals?.categoryHeaders?.[dataPointIndex] ||
    w?.globals?.labels?.[dataPointIndex] ||
    '';
  return normalizeLabel(fromCategories || fromGlobals, dataPointIndex, 'Point');
};

/**
 * SimulationApexChart - scenario forecast with a shaded confidence band.
 */
const SimulationApexChart = ({ data, onChartContextChange }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const mountedRef = useRef(true);
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!data || !data.labels || !containerRef.current) return;

    const labels = (data.labels || []).map((label, index) => normalizeLabel(label, index, 'Point'));
    const values = labels.map((_, index) => {
      const raw = Array.isArray(data.values) ? data.values[index] : null;
      return raw == null ? null : Number(raw);
    });
    const valueSeries = buildXYSeries(labels, values);
    const confidenceUpperSeries = values.map((value, index) => {
      const raw = Array.isArray(data.confidence_upper) ? data.confidence_upper[index] : null;
      return toNumber(raw) ?? Math.round((toNumber(value) ?? 0) * 1.12);
    });
    const confidenceLowerSeries = values.map((value, index) => {
      const raw = Array.isArray(data.confidence_lower) ? data.confidence_lower[index] : null;
      return toNumber(raw) ?? Math.round((toNumber(value) ?? 0) * 0.88);
    });
    const confidenceBand = buildConfidenceBand(labels, values, data.confidence_upper, data.confidence_lower);
    const emitChartContext = (visibleRange = null) => {
      if (typeof onChartContextChange !== 'function') return;
      onChartContextChange({
        graph_summary: buildRenderedGraphSummary(labels, values, confidenceLowerSeries, confidenceUpperSeries, visibleRange),
      });
    };
    emitChartContext();

    const series = [
      {
        name: 'Confidence Band',
        type: 'rangeArea',
        data: confidenceBand,
      },
      {
        name: 'Hybrid Scenario Forecast',
        type: 'line',
        data: valueSeries,
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 380,
        toolbar: {
          show: true,
          autoSelected: 'zoom',
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true,
          },
        },
        animations: { enabled: true, easing: 'easeinout', speed: 600 },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
        events: {
          zoomed: (_chartContext, { xaxis }) => {
            emitChartContext(xaxis);
          },
          beforeResetZoom: () => {
            emitChartContext();
            setTimeout(() => {
              if (mountedRef.current) {
                setChartKey((key) => key + 1);
              }
            }, 0);
          },
        },
      },
      series,
      stroke: {
        curve: 'smooth',
        width: [0, 3],
        dashArray: [0, 0],
        spanNulls: true,
      },
      colors: ['#7C3AED', '#0057B8'],
      fill: {
        type: ['solid', 'solid'],
        opacity: [0.18, 1],
      },
      xaxis: {
        type: 'category',
        categories: labels,
        tickAmount: Math.min(labels.length, 10),
        labels: {
          rotate: -35,
          style: { fontSize: '11px', colors: '#64748B' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: 'Demand Quantity',
          style: { fontSize: '12px', color: '#475569', fontWeight: 600 },
        },
        labels: {
          formatter: (val) => {
            if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
            if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
            return Math.round(val).toLocaleString();
          },
          style: { colors: '#64748B', fontSize: '11px' },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        custom: ({ series, dataPointIndex, w }) => {
          const safeIndex = Number.isInteger(dataPointIndex) && dataPointIndex >= 0 ? dataPointIndex : 0;
          const label = getTooltipLabel(w, safeIndex);
          const collapsed = new Set(w?.globals?.collapsedSeriesIndices || []);
          let html = `<div class="apexcharts-tooltip-title" style="font-family: Inter, sans-serif; font-size: 12px; font-weight: 600; padding: 6px 10px; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; color: #475569;">${label}</div>`;

          const rows = [
            { name: 'Confidence Band', value: formatTooltipBand(confidenceLowerSeries[safeIndex], confidenceUpperSeries[safeIndex]) },
            { name: 'Hybrid Scenario Forecast', value: formatTooltipUnits(values[safeIndex]) },
          ];

          rows.forEach((row, idx) => {
            if (collapsed.has(idx)) return;

            const color = w?.globals?.colors?.[idx] || '#64748B';
            html += `
              <div class="apexcharts-tooltip-series-group apexcharts-active" style="display: flex; align-items: center; padding: 6px 10px; gap: 8px;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${color}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0;"></span>
                <div class="apexcharts-tooltip-text" style="font-family: Inter, sans-serif; font-size: 12px; display: flex; align-items: center; gap: 4px; width: 100%;">
                  <span class="apexcharts-tooltip-text-y-label" style="color: #64748B;">${row.name}:</span>
                  <span class="apexcharts-tooltip-text-y-value" style="font-weight: 600; color: #1E293B;">${row.value}</span>
                </div>
              </div>
            `;
          });

          return html;
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        offsetX: -20,
        markers: { radius: 3 },
        labels: { colors: '#374151' },
      },
      grid: {
        borderColor: '#F1F5F9',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
      },
      markers: {
        size: [0, 3],
        strokeWidth: 0,
        hover: { size: 5 },
      },
    };

    const chart = new ApexCharts(containerRef.current, options);
    chart.render();
    chartRef.current = chart;

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, chartKey, onChartContextChange]);

  if (!data || !data.labels) {
    return (
      <div className="chart-loading-state">
        <p style={{ color: '#94A3B8' }}>No simulation data available</p>
      </div>
    );
  }

  return (
    <div>
      <div ref={containerRef} />
      {data.last_updated && (
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '0.5rem', textAlign: 'right' }}>
          Last updated: {new Date(data.last_updated).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default SimulationApexChart;
